import React, { createContext, useContext, useState, useEffect, useCallback } from 'react'
import { exchangeRatesAPI } from '../lib/api'

const CurrencyContext = createContext(null)

const CURRENCIES = {
  USD: { code: 'USD', symbol: '$', name: 'US Dollar', flag: 'US', position: 'before' },
  EUR: { code: 'EUR', symbol: '\u20AC', name: 'Euro', flag: 'EU', position: 'before' },
  GBP: { code: 'GBP', symbol: '\u00A3', name: 'British Pound', flag: 'GB', position: 'before' },
  AED: { code: 'AED', symbol: '\u062F.\u0625', name: 'UAE Dirham', flag: 'AE', position: 'after' },
}

export function CurrencyProvider({ children }) {
  const [currency, setCurrencyState] = useState(() => {
    const saved = localStorage.getItem('2kenize_currency')
    return saved && CURRENCIES[saved] ? saved : 'USD'
  })
  const [rates, setRates] = useState({ USD: 1, EUR: 0.92, GBP: 0.79, AED: 3.67 })
  const [ratesLoaded, setRatesLoaded] = useState(false)

  const fetchRates = useCallback(async () => {
    try {
      const res = await exchangeRatesAPI.getRates()
      if (res.data?.rates) {
        setRates(res.data.rates)
        setRatesLoaded(true)
      }
    } catch (error) {
      console.error('Failed to fetch exchange rates:', error)
      setRatesLoaded(true)
    }
  }, [])

  useEffect(() => {
    fetchRates()
    const interval = setInterval(fetchRates, 60 * 60 * 1000)
    return () => clearInterval(interval)
  }, [fetchRates])

  const setCurrency = (code) => {
    if (CURRENCIES[code]) {
      setCurrencyState(code)
      localStorage.setItem('2kenize_currency', code)
    }
  }

  const convert = useCallback((amountUSD) => {
    if (amountUSD == null || isNaN(amountUSD)) return 0
    const rate = rates[currency] || 1
    return amountUSD * rate
  }, [currency, rates])

  const formatPrice = useCallback((amountUSD, opts = {}) => {
    if (amountUSD == null || isNaN(amountUSD)) return '-'
    const { decimals = 0, compact = false } = opts
    const converted = convert(amountUSD)
    const info = CURRENCIES[currency]

    let formatted
    if (compact && converted >= 1_000_000) {
      formatted = (converted / 1_000_000).toFixed(1) + 'M'
    } else if (compact && converted >= 1_000) {
      formatted = (converted / 1_000).toFixed(1) + 'K'
    } else {
      formatted = converted.toLocaleString(undefined, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    }

    if (info.position === 'after') {
      return `${formatted} ${info.symbol}`
    }
    return `${info.symbol}${formatted}`
  }, [currency, convert])

  return (
    <CurrencyContext.Provider value={{
      currency,
      setCurrency,
      currencies: CURRENCIES,
      rates,
      ratesLoaded,
      convert,
      formatPrice,
      currencyInfo: CURRENCIES[currency],
    }}>
      {children}
    </CurrencyContext.Provider>
  )
}

export function useCurrency() {
  const context = useContext(CurrencyContext)
  if (!context) {
    throw new Error('useCurrency must be used within a CurrencyProvider')
  }
  return context
}
